import React from "react";
import {
  Card,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Button,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  UnorderedListOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  TransportType,
  Port,
  ContainerSize,
  BookingType,
} from "../utils/types";

const { Option } = Select;
const { Text } = Typography;

export type ViewMode = "timeline" | "calendar";

export interface TariffFilterValues {
  transportType: number | null;
  originPort: number | null;
  destinationPort: number | null;
  bookingType: number | null;
  containerSize: number | null;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | [dayjs.Dayjs, null];
  viewMode: ViewMode;
  showActiveTariffs: boolean;
}

interface TariffFilterFormProps {
  transportTypes: TransportType[];
  originPorts: Port[];
  destinationPorts: Port[];
  containerSizes: ContainerSize[];
  bookingTypes: BookingType[];
  values: TariffFilterValues;
  onChange: (values: Partial<TariffFilterValues>) => void;
  showViewModeToggle?: boolean;
}

const TariffFilterForm: React.FC<TariffFilterFormProps> = ({
  transportTypes,
  originPorts,
  destinationPorts,
  containerSizes,
  bookingTypes,
  values,
  onChange,
  showViewModeToggle = false,
}) => {
  const handleActiveTariffsClick = () => {
    // Set date range to show only active and future tariffs
    // Use the start of today to ensure proper date comparison
    const today = dayjs().startOf("day");

    onChange({
      dateRange: [today, null],
      showActiveTariffs: true,
    });
  };

  return (
    <Card title="Filter Tariffs" style={{ marginBottom: 24 }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Transport Type</Text>
            </div>
            <Select
              placeholder="Select Transport Type"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={values.transportType}
              onChange={(value) => onChange({ transportType: value })}
            >
              {transportTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Origin Port</Text>
            </div>
            <Select
              placeholder="Select Origin Port"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={values.originPort}
              onChange={(value) => onChange({ originPort: value })}
            >
              {originPorts.map((port) => (
                <Option key={port.id} value={port.id}>
                  {port.name} ({port.country})
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Destination Port</Text>
            </div>
            <Select
              placeholder="Select Destination Port"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={values.destinationPort}
              onChange={(value) => onChange({ destinationPort: value })}
            >
              {destinationPorts.map((port) => (
                <Option key={port.id} value={port.id}>
                  {port.name} ({port.country})
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Valid Date Range</Text>
              <div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Filter tariffs active during this period
                </Text>
              </div>
            </div>
            <Space style={{ width: "100%" }}>
              <DatePicker
                placeholder="From Date"
                style={{ width: "100%" }}
                value={values.dateRange[0]}
                onChange={(date) => {
                  onChange({
                    dateRange: [date || dayjs(), values.dateRange[1]],
                    showActiveTariffs: false,
                  });
                }}
              />
              <DatePicker
                placeholder="To Date (Optional)"
                style={{ width: "100%" }}
                value={values.dateRange[1] || null}
                onChange={(date) => {
                  onChange({
                    dateRange: [values.dateRange[0], date],
                    showActiveTariffs: false,
                  });
                }}
              />
              <Tooltip title="Show currently active and future tariffs">
                <Button
                  type={values.showActiveTariffs ? "primary" : "default"}
                  icon={<ClockCircleOutlined />}
                  onClick={handleActiveTariffsClick}
                >
                  Active Now
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Booking Type</Text>
            </div>
            <Select
              placeholder="Select Booking Type"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={values.bookingType}
              onChange={(value) => onChange({ bookingType: value })}
            >
              {bookingTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Container Size</Text>
            </div>
            <Select
              placeholder="Select Container Size"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={values.containerSize}
              onChange={(value) => onChange({ containerSize: value })}
            >
              {containerSizes.map((size) => (
                <Option key={size.id} value={size.id}>
                  {size.name}
                </Option>
              ))}
            </Select>
          </Col>

          {showViewModeToggle && (
            <Col xs={24} sm={12} lg={8} xl={6}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>View Mode</Text>
              </div>
              <Space>
                <Button
                  type={values.viewMode === "timeline" ? "primary" : "default"}
                  icon={<UnorderedListOutlined />}
                  onClick={() => onChange({ viewMode: "timeline" })}
                >
                  Timeline
                </Button>
                <Button
                  type={values.viewMode === "calendar" ? "primary" : "default"}
                  icon={<CalendarOutlined />}
                  onClick={() => onChange({ viewMode: "calendar" })}
                >
                  Calendar
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Space>
    </Card>
  );
};

export default TariffFilterForm;
